import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Edit, Plus, Trash2, Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Checkbox } from '@/components/ui/checkbox'
import { lookupsService } from '@/services/lookupsService'
import { CodeType, Lookup } from '@/api/types'

/**
 * Lookups Management Page
 * Complete form for managing lookup types and their values
 */
export const Lookups: React.FC = () => {
  const { t } = useTranslation('common')
  // State for code types
  const [codeTypes, setCodeTypes] = useState<CodeType[]>([])
  const [selectedCodeTypeId, setSelectedCodeTypeId] = useState<string>('')
  const [isEditingCodeType, setIsEditingCodeType] = useState(false)
  const [newCodeTypeName, setNewCodeTypeName] = useState('')

  // State for master/parent lookups
  const [allLookups, setAllLookups] = useState<Lookup[]>([])
  const [masterLookups, setMasterLookups] = useState<SearchableSelectOption[]>([])
  const [selectedParentId, setSelectedParentId] = useState<string>('0') // 0 means "Self"

  // State for lookup rows
  const [lookupRows, setLookupRows] = useState<Lookup[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  // Load code types on mount
  useEffect(() => {
    loadCodeTypes()
    loadAllLookups()
  }, [])

  // Load lookups when code type or parent changes
  useEffect(() => {
    if (selectedCodeTypeId && selectedCodeTypeId !== 'add-new') {
      loadLookups()
    } else {
      // Reset rows when "Add New Lookup" is selected
      setLookupRows([
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
      ])
    }
  }, [selectedCodeTypeId, selectedParentId])

  // Update master lookups when all lookups change (no need to depend on selectedCodeTypeId)
  useEffect(() => {
    updateMasterLookups()
  }, [allLookups])

  const loadCodeTypes = async () => {
    try {
      setLoading(true)
      const types = await lookupsService.getAllCodeTypes()
      console.log('🔵 API Response - getAllCodeTypes:', types)
      console.log('🔵 Response type:', typeof types, 'Is Array:', Array.isArray(types))
      
      if (types && types.length > 0) {
        // Log first item to see structure
        console.log('🔵 First code type structure:', JSON.stringify(types[0], null, 2))
        console.log('🔵 First code type keys:', Object.keys(types[0]))
      }
      
      // Filter and normalize types - handle both camelCase and PascalCase
      const validTypes = (Array.isArray(types) ? types : [])
        .filter((type: any) => {
          const hasId = type && (type.id || type.Id || type.ID)
          if (!hasId) {
            console.warn('⚠️ Type filtered out (no ID):', type)
          }
          return hasId
        })
        .map((type: any) => {
          const normalized = {
            id: type.id || type.Id || type.ID || 0,
            lookupTypeName: type.lookupTypeName || type.LookupTypeName || type.lookupTypeNameEn || type.LookupTypeNameEn || type.typeNameEn || type.TypeNameEn || type.name || type.Name || '',
            isActive: type.isActive !== undefined ? type.isActive : (type.IsActive !== undefined ? type.IsActive : true),
          }
          console.log('🔵 Normalized type:', normalized)
          return normalized
        })
        .filter((type) => {
          const isValid = type.id > 0 && type.lookupTypeName
          if (!isValid) {
            console.warn('⚠️ Type filtered out (invalid):', type)
          }
          return isValid
        })
      
      console.log('✅ Valid code types after normalization:', validTypes.length, validTypes)
      setCodeTypes(validTypes)
      
      if (validTypes.length === 0 && types && types.length > 0) {
        console.error('❌ No valid code types found after filtering. First item:', JSON.stringify(types[0], null, 2))
        toast.error('Code types loaded but could not be processed. Check console for details.')
      }
    } catch (error: any) {
      console.error('❌ Error loading code types:', error)
      console.error('❌ Error response:', error.response?.data)
      toast.error(error.response?.data?.message || 'Failed to load lookup types')
      setCodeTypes([])
    } finally {
      setLoading(false)
    }
  }

  const loadAllLookups = async () => {
    try {
      const lookups = await lookupsService.getAllLookups()
      
      // Normalize lookups - handle both camelCase and PascalCase
      const validLookups = (Array.isArray(lookups) ? lookups : [])
        .filter((lookup: any) => lookup && (lookup.lookupCodeId !== undefined || lookup.LookupCodeId !== undefined))
        .map((lookup: any) => ({
          id: lookup.id || lookup.Id,
          lookupCodeId: lookup.lookupCodeId || lookup.LookupCodeId || 0,
          lookupNameEn: lookup.lookupNameEn || lookup.LookupNameEn || '',
          lookupNameAr: lookup.lookupNameAr || lookup.LookupNameAr || '',
          parentId: lookup.parentId || lookup.ParentId || 0,
          isActive: lookup.isActive !== undefined ? lookup.isActive : (lookup.IsActive !== undefined ? lookup.IsActive : true),
        }))
      
      setAllLookups(validLookups)
    } catch (error: any) {
      // Don't show toast for this as it's a background operation
      setAllLookups([])
    }
  }

  const loadLookups = async () => {
    if (!selectedCodeTypeId || selectedCodeTypeId === 'add-new') return

    try {
      setLoading(true)
      const codeTypeId = parseInt(selectedCodeTypeId)
      const parentId = selectedParentId === '0' ? 0 : parseInt(selectedParentId)
      
      // Always call API with both lookupCodeId (from Types dropdown) and parentId (from Master dropdown)
      const lookups = await lookupsService.getLookupsById(codeTypeId, parentId)

      // Normalize and filter lookups - handle both camelCase and PascalCase
      // API returns parentCode, not parentId
      const validLookups = Array.isArray(lookups) ? lookups
        .map((lookup: any) => {
          // Handle different field name variations
          const lookupParentId = lookup.parentId || lookup.ParentId || lookup.parentCode || lookup.ParentCode || 0
          const lookupCodeId = lookup.lookupCodeId || lookup.LookupCodeId || 0
          
          return {
            id: lookup.id || lookup.Id,
            lookupCodeId: lookupCodeId,
            lookupNameEn: lookup.lookupNameEn || lookup.LookupNameEn || '',
            lookupNameAr: lookup.lookupNameAr || lookup.LookupNameAr || '',
            parentId: lookupParentId,
            isActive: lookup.isActive !== undefined ? lookup.isActive : (lookup.IsActive !== undefined ? lookup.IsActive : (lookup.isActive === 1 || lookup.IsActive === 1)),
          }
        })
        .filter((lookup: Lookup) => {
          // Filter by both codeTypeId and parentId, and ensure it has a name
          return lookup && 
                 lookup.lookupCodeId === codeTypeId && 
                 lookup.parentId === parentId && 
                 (lookup.lookupNameEn?.trim() || lookup.lookupNameAr?.trim())
        }) : []

      // Reset deleted rows when loading new data
      setDeletedRowIndices(new Set())

      if (validLookups.length > 0) {
        setLookupRows(validLookups)
      } else {
        // Show 3 empty rows if no data - these will be for the selected type with the selected parent
        setLookupRows([
          { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
          { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
          { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
        ])
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('lookups.failedToLoadLookups'))
      // Show 3 empty rows on error
      const codeTypeId = parseInt(selectedCodeTypeId)
      const parentId = selectedParentId === '0' ? 0 : parseInt(selectedParentId)
      setLookupRows([
        { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
        { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
        { lookupCodeId: codeTypeId, lookupNameEn: '', lookupNameAr: '', parentId, isActive: true },
      ])
      setDeletedRowIndices(new Set())
    } finally {
      setLoading(false)
    }
  }

  const updateMasterLookups = () => {
    const options: SearchableSelectOption[] = [
      { value: '0', label: t('lookups.self') }
    ]

    // Add ALL lookups from ALL types as master options
    allLookups.forEach(lookup => {
      if (lookup && lookup.id && (lookup.lookupNameEn || lookup.lookupNameAr)) {
        options.push({
          value: lookup.id.toString(),
          label: lookup.lookupNameEn || lookup.lookupNameAr || 'Unnamed'
        })
      }
    })

    setMasterLookups(options)
  }

  const handleCodeTypeChange = (value: string) => {
    setSelectedCodeTypeId(value)
    setIsEditingCodeType(false)
    setNewCodeTypeName('')
    setDeletedRowIndices(new Set())
    
    if (value === 'add-new') {
      setSelectedParentId('0')
      setLookupRows([
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
        { lookupCodeId: 0, lookupNameEn: '', lookupNameAr: '', parentId: 0, isActive: true },
      ])
    } else {
      setSelectedParentId('0')
    }
  }

  const handleEditCodeType = () => {
    if (!selectedCodeTypeId || selectedCodeTypeId === 'add-new') {
      toast.error(t('lookups.pleaseSelectLookupType'))
      return
    }

    const selectedType = codeTypes.find(type => type.id.toString() === selectedCodeTypeId)
    if (selectedType) {
      setIsEditingCodeType(true)
      setNewCodeTypeName(selectedType.lookupTypeName)
    }
  }

  const handleParentChange = (value: string) => {
    setSelectedParentId(value)
    setDeletedRowIndices(new Set())
    // loadLookups will be called by useEffect when selectedParentId changes
  }

  const handleAddRows = (count: number) => {
    console.log('🔵 Adding rows:', count, 'selectedCodeTypeId:', selectedCodeTypeId)
    
    const parentId = selectedParentId === '0' ? 0 : parseInt(selectedParentId)
    const codeTypeId = selectedCodeTypeId === 'add-new' ? 0 : parseInt(selectedCodeTypeId)
    
    const newRows: Lookup[] = Array.from({ length: count }, () => ({
      lookupCodeId: codeTypeId,
      lookupNameEn: '',
      lookupNameAr: '',
      parentId,
      isActive: true,
    }))

    console.log('🔵 New rows to add:', newRows)
    setLookupRows(prevRows => {
      const updated = [...prevRows, ...newRows]
      console.log('🔵 Updated rows:', updated.length)
      return updated
    })
  }

  const handleDeleteRow = (index: number) => {
    const row = lookupRows[index]
    
    // If row is already marked for deletion, undo it
    if (deletedRowIndices.has(index)) {
      setDeletedRowIndices(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
    } else {
      // Mark row for deletion (only if it has an ID, otherwise just remove it)
      if (row.id) {
        setDeletedRowIndices(prev => new Set(prev).add(index))
      } else {
        // If no ID, just remove it from the array
        setLookupRows(prevRows => {
          const newRows = [...prevRows]
          newRows.splice(index, 1)
          return newRows
        })
      }
    }
  }

  const handleRowChange = (index: number, field: keyof Lookup, value: any) => {
    setLookupRows(prevRows => {
      const newRows = [...prevRows]
      newRows[index] = { ...newRows[index], [field]: value }
      return newRows
    })
  }

  const handleSave = async () => {
    // Validate lookup type name for new types
    if (selectedCodeTypeId === 'add-new') {
      if (!newCodeTypeName.trim()) {
        toast.error(t('lookups.pleaseEnterLookupTypeName'))
        return
      }
    } else if (!selectedCodeTypeId) {
      toast.error(t('lookups.pleaseSelectLookupType'))
      return
    }

    try {
      setSaving(true)

      const parentId = selectedParentId === '0' ? 0 : parseInt(selectedParentId)
      const codeTypeId = selectedCodeTypeId === 'add-new' ? 0 : parseInt(selectedCodeTypeId)

      // Create array with original indices to track deletion status
      const rowsWithIndex = lookupRows.map((row, originalIndex) => ({
        row,
        originalIndex,
        isDeleted: deletedRowIndices.has(originalIndex)
      }))
      
      // Filter out empty rows - only include rows with at least one name or deleted rows
      const rowsToSave = rowsWithIndex.filter(({ row, isDeleted }) => {
        // Include deleted rows
        if (isDeleted) return true
        // Include rows with at least one name filled
        const hasName = (row.lookupNameEn?.trim() || '').length > 0 || (row.lookupNameAr?.trim() || '').length > 0
        return hasName
      })
      
      // Remove duplicate rows based on ID to prevent adding same row twice
      const uniqueRowsToSave = rowsToSave.filter(({ row }, index, self) => {
        // If row has an ID, check if it's the first occurrence
        if (row.id) {
          return index === self.findIndex(({ row: r }) => r.id === row.id)
        }
        // If no ID, keep all (they are new rows)
        return true
      })

      // Allow saving even if no lookup rows are provided (for new type creation)
      // User can create a type without lookups if they want

      // Prepare payload according to Swagger API structure
      // Structure: { lookUpTypes: {...}, lookupss: [...] }
      const validLookups = uniqueRowsToSave
        .map(({ row, isDeleted }) => {
          
          // Skip rows with no names (unless marked for deletion)
          if (!isDeleted && !row.lookupNameEn?.trim() && !row.lookupNameAr?.trim()) {
            return null
          }
          
          // Always use selected type (lookupCodeId) and selected master (parentId) from dropdowns
          // This ensures consistency - all lookups in this context belong to the selected type and master
          const lookupItem: any = {
            id: row.id || 0,
            lookupCodeId: codeTypeId, // From Types dropdown
            lookupNameEn: row.lookupNameEn?.trim() || '',
            lookupNameAr: row.lookupNameAr?.trim() || '',
            isActive: row.isActive === true || (typeof row.isActive === 'number' && row.isActive === 1) ? 1 : 0,
            parentCode: parentId, // From Master dropdown
            isDeleted: isDeleted,
          }
          
          return lookupItem
        })
        .filter(item => item !== null) // Remove null items

      // Get type name - for new types use input, for existing use current name or edited name
      let typeName = ''
      if (selectedCodeTypeId === 'add-new') {
        typeName = newCodeTypeName.trim()
      } else if (isEditingCodeType) {
        typeName = newCodeTypeName.trim()
      } else {
        const currentType = codeTypes.find(t => t.id === codeTypeId)
        typeName = currentType?.lookupTypeName || ''
      }

      // Determine action: 'save' or 'update'
      // Action should be 'save' only when:
      // 1. New type is being created (selectedCodeTypeId === 'add-new')
      // 2. Type exists but all rows are new (id = 0) - first time data insertion for existing type
      // Action should be 'update' when:
      // 1. Existing rows with IDs are being edited/updated
      // 2. Rows are being deleted
      // 3. Type name is being edited
      // 4. New rows are added to existing data (when some rows already have IDs)
      const hasExistingRows = validLookups.some(lookup => lookup.id && lookup.id > 0)
      const allRowsAreNew = validLookups.length > 0 && validLookups.every(lookup => !lookup.id || lookup.id === 0)
      const isNewType = selectedCodeTypeId === 'add-new'
      const isTypeNameEdited = isEditingCodeType && selectedCodeTypeId !== 'add-new'
      const hasDeletedRows = validLookups.some(lookup => lookup.isDeleted === true)
      
      // Determine action
      let action: 'save' | 'update' = 'save'
      if (isNewType) {
        // New type creation - always save
        action = 'save'
      } else if (allRowsAreNew && !hasExistingRows && !isTypeNameEdited && !hasDeletedRows) {
        // Type exists, all rows are new (first time insertion) - save
        action = 'save'
      } else {
        // Any of these conditions means update:
        // - Has existing rows with IDs
        // - Type name is being edited
        // - Rows are being deleted
        // - Mix of new and existing rows
        action = 'update'
      }

      // Build payload structure matching Swagger
      const payload: any = {
        lookUpTypes: {
          id: codeTypeId,
          typeNameEn: typeName,
          action: action,
        },
        lookupss: validLookups,
      }
      
      await lookupsService.saveLookups(payload)

      toast.success(t('lookups.lookupsSavedSuccess'))
      
      // Reload data
      await loadCodeTypes()
      await loadAllLookups()
      
      // Reset deleted rows after successful save
      setDeletedRowIndices(new Set())
      
      // Reset form if it was a new code type
      if (selectedCodeTypeId === 'add-new') {
        setSelectedCodeTypeId('')
        setNewCodeTypeName('')
        setIsEditingCodeType(false)
      } else {
        // Reload lookups for the current selection
        await loadLookups()
      }
    } catch (error: any) {
      // Show more detailed error message
      const errorMessage = error.response?.data?.msg || 
                          error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          t('lookups.failedToLoadLookups')
      
      toast.error(t('lookups.saveFailed', { message: errorMessage }))
    } finally {
      setSaving(false)
    }
  }

  // Prepare code type options for dropdown
  const codeTypeOptions: SearchableSelectOption[] = [
    { value: 'add-new', label: t('lookups.addNewLookUp') },
    ...codeTypes
      .filter(type => {
        const isValid = type && type.id && type.lookupTypeName
        return isValid
      })
      .map(type => ({
        value: type.id.toString(),
        label: type.lookupTypeName || 'Unnamed',
      })),
  ]

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <PageHeader
            title={t('lookups.title')}
            description={t('lookups.description')}
          />
        </div>
        <div>
          <Button
            onClick={handleEditCodeType}
            variant="outline"
            className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white border-0"
          >
            <Edit className="w-4 h-4 mr-2" />
            {t('lookups.editLookUpType')}
          </Button>
        </div>
      </div>

      {/* LOOKUP TYPE Section */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b">
          <CardTitle className="text-lg font-semibold">{t('lookups.lookupType')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Types Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="types">{t('lookups.types')}</Label>
              <SearchableSelect
                options={codeTypeOptions}
                value={selectedCodeTypeId}
                onValueChange={handleCodeTypeChange}
                placeholder={t('lookups.selectLookupType')}
                searchPlaceholder={t('lookups.searchLookupTypes')}
                disabled={loading}
                emptyMessage={codeTypeOptions.length === 1 ? t('lookups.noLookupTypesAvailable') : t('lookups.noResultsFound')}
              />
            </div>

            {/* Master Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="master">{t('lookups.master')}</Label>
              <SearchableSelect
                options={masterLookups}
                value={selectedParentId}
                onValueChange={handleParentChange}
                placeholder={t('lookups.selectMaster')}
                searchPlaceholder={t('lookups.searchMaster')}
                disabled={loading}
                emptyMessage={t('lookups.noMasterLookupsAvailable')}
              />
            </div>
          </div>

          {/* Hidden input for new code type name */}
          {(selectedCodeTypeId === 'add-new' || isEditingCodeType) && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="codeTypeName">{t('lookups.lookupTypeName')}</Label>
              <Input
                id="codeTypeName"
                value={newCodeTypeName}
                onChange={(e) => setNewCodeTypeName(e.target.value)}
                placeholder={t('lookups.enterLookupTypeName')}
                className="max-w-md"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lookups Section */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">{t('lookups.lookups')}</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddRows(1)}
                disabled={loading}
                className="bg-white hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('lookups.addRow')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddRows(3)}
                disabled={loading}
                className="bg-white hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('lookups.add3Rows')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddRows(5)}
                disabled={loading}
                className="bg-white hover:bg-gray-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('lookups.add5Rows')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {lookupRows.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {t('lookups.noLookupsFound')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">{t('lookups.lookupNameEn')}</th>
                        <th className="text-left p-3 font-semibold">{t('lookups.lookupNameAr')}</th>
                        <th className="text-left p-3 font-semibold">{t('lookups.isActive')}</th>
                        <th className="text-left p-3 font-semibold w-20">{t('lookups.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupRows.map((row, index) => {
                        const isDeleted = deletedRowIndices.has(index)
                        
                        return (
                          <tr 
                            key={index} 
                            className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                              isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                            }`}
                          >
                            <td className="p-3">
                              <Input
                                value={row.lookupNameEn}
                                onChange={(e) => handleRowChange(index, 'lookupNameEn', e.target.value)}
                                placeholder={t('lookups.enterEnglishName')}
                                className="w-full"
                                disabled={saving || isDeleted}
                              />
                            </td>
                            <td className="p-3">
                              <Input
                                value={row.lookupNameAr}
                                onChange={(e) => handleRowChange(index, 'lookupNameAr', e.target.value)}
                                placeholder={t('lookups.enterArabicName')}
                                className="w-full"
                                disabled={saving || isDeleted}
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`active-${index}`}
                                  checked={row.isActive}
                                  onCheckedChange={(checked) => 
                                    handleRowChange(index, 'isActive', checked)
                                  }
                                  disabled={saving || isDeleted}
                                />
                                <Label htmlFor={`active-${index}`} className="cursor-pointer">
                                  {row.isActive ? t('lookups.active') : t('lookups.inactive')}
                                </Label>
                              </div>
                            </td>
                            <td className="p-3">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteRow(index)}
                                disabled={saving}
                                className={isDeleted 
                                  ? "text-green-600 hover:text-green-700 hover:bg-green-50" 
                                  : "text-destructive hover:text-destructive hover:bg-destructive/10"
                                }
                                title={isDeleted ? t('lookups.undoDelete') : t('lookups.deleteRow')}
                              >
                                {isDeleted ? (
                                  <span className="text-xs font-semibold">{t('lookups.undo')}</span>
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Save/Update Button */}
          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || loading || !selectedCodeTypeId}
              className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white min-w-[150px]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('lookups.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {lookupRows.some(r => r.id && !r.delete) ? t('lookups.updateLookup') : t('lookups.saveLookup')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
